import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  AdjustmentsHorizontalIcon,
  BuildingOfficeIcon,
  DevicePhoneMobileIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  CloudIcon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'
import psgLogo from '../assets/images/paysafego.png'

// Nav is grouped into sections so related items live together. Each section
// renders its label as a sticky header above its items.
const navSections = [
  {
    name: 'Tenant Onboarding',
    items: [
      {
        name: 'Pending setups',
        href: '/admin/pending-tenant-setups',
        icon: ClipboardDocumentCheckIcon,
        title: 'Drafts captured before tenants are provisioned',
      },
      {
        name: 'Live tenants',
        href: '/admin/tenants',
        icon: BuildingOfficeIcon,
        title: 'Tenants already provisioned and running',
      },
    ],
  },
  {
    name: 'Platform',
    items: [
      { name: 'Platform config', href: '/admin/platform-config', icon: AdjustmentsHorizontalIcon },
      { name: 'Billable usage',  href: '/admin/billable-usage',  icon: BanknotesIcon,           title: 'Usage and rates across all tenants' },
      { name: 'Device queue',    href: '/admin/mobile-devices',  icon: DevicePhoneMobileIcon,   title: 'Pending device approvals across all tenants' },
      { name: 'Storage',         href: '/admin/storage',         icon: CloudIcon,               title: 'Platform and tenant blob storage (all tenants)' },
    ],
  },
]

export default function AdminLayout({ children }) {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const { user, logout } = useAuth()

  return (
    <div className="min-h-screen flex bg-gray-50">
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex h-14 items-center gap-2 border-b border-gray-200 bg-psg-teal px-3">
        <button type="button" className="text-white p-2" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
        </button>
        <img src={psgLogo} alt="" className="h-8" />
        <span className="text-white text-sm font-medium truncate">Platform</span>
      </div>

      <aside
        className={`fixed inset-y-0 left-0 z-30 w-56 transform bg-psg-teal text-white transition-transform lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="hidden lg:flex flex-col px-4 py-5 border-b border-white/10">
          <img src={psgLogo} alt="PaySafeGO" className="h-10 w-auto object-contain" />
          <div className="text-[10px] text-gray-300/70 font-mono mt-1">
            v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0'}
          </div>
        </div>
        <nav className="mt-16 lg:mt-0 px-2 py-4 space-y-4">
          {navSections.map((section) => (
            <div key={section.name}>
              <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-300/70">
                {section.name}
              </div>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const active = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      title={item.title}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                        active ? 'bg-psg-yellow text-psg-teal-900' : 'text-gray-200 hover:bg-white/10'
                      }`}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {item.name}
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="absolute bottom-0 inset-x-0 p-3 border-t border-white/10 space-y-1">
          <div className="flex items-center gap-2 px-2 py-1 text-xs text-gray-300 truncate">
            <UserIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">{user?.username || '—'}</span>
          </div>
          <button
            type="button"
            onClick={() => logout()}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-200 hover:bg-white/10"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            Sign out
          </button>
        </div>
      </aside>

      {open && (
        <button
          type="button"
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
        />
      )}

      <main className="flex-1 min-h-screen pt-14 pb-8 px-4 sm:px-6 lg:pt-8 lg:pb-10 lg:pl-[calc(14rem+2rem)] lg:pr-8">
        {children}
      </main>
    </div>
  )
}
